import { TimeLineEvent, TimelineSpan, TimelineSpanParent } from '../domain/TimelineSpan'

class SpanService {
    public sort = (spans: TimelineSpan[]): TimelineSpan[] => {
        if (spans.length === 0) {
            return []
        }

        const sortBy = key => (a, b) => (a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : 0)

        const sortedSpans = spans.sort(sortBy('cs'))

        for (let i = 0; i < sortedSpans.length; ++i) {
            for (let j = i - 1; j >= 0; --j) {
                if (
                    this.contains(sortedSpans[j], sortedSpans[i]) &&
                    (!this.isRepeatSpan(sortedSpans[j]) || (this.isRepeatSpan(sortedSpans[i]) && this.isRepeatSpan(sortedSpans[j])))
                ) {
                    sortedSpans[i].parent = sortedSpans[j].id
                    sortedSpans[i].parentTick = sortedSpans[j].tick
                    break
                }
            }
        }

        const tickDependencies = {}
        for (const span of sortedSpans) {
            if (!span.tick) {
                continue
            }
            if (!tickDependencies[span.tick]) {
                tickDependencies[span.tick] = {
                    id: span.tick,
                    cs: Math.pow(2, 25) - 1,
                    dependencies: [],
                }
            }

            if (span.parent) {
                tickDependencies[span.tick].dependencies.push(span.parentTick)
            }
            tickDependencies[span.tick].cs = Math.min(tickDependencies[span.tick].cs, span.cs)
        }

        const tickList = (Object.entries(tickDependencies) as any)
            .flat()
            .filter(x => x.id !== undefined)
            .sort(sortBy('cs'))
        for (const tick of tickList) {
            tick.dependencies = tick.dependencies.filter((value, index, sourceArray) => sourceArray.indexOf(value) === index)
        }

        const sortedTicks = [tickList.shift()]

        if (sortedTicks.length === 0) {
            return []
        }

        const collectedTicks = [sortedTicks[0].id]
        // eslint-disable-line
        tickLoop: while (tickList.length) {
            for (let i = 0; i < tickList.length; ++i) {
                // Tick for which all dependencies already have been collected
                if (tickList[i].dependencies.filter(element => !collectedTicks.includes(element)).length === 0) {
                    const ticks = tickList.splice(i, 1)[0]
                    collectedTicks.push(ticks.id)
                    sortedTicks.push(ticks)
                    continue tickLoop // eslint-disable-line
                }
            }

            // If we did not find a tick to add we just add the first one so
            // that we get a deterministic loop
            const tick = tickList.shift()
            collectedTicks.push(tick.id)
            sortedTicks.push(tick)
        }

        return sortedTicks.map(tick => sortedSpans.filter(e => e.tick === tick.id)).flat()
    }

    public getParents = (spans: TimelineSpan[], span: TimelineSpan): TimelineSpanParent[] => {
        const parents: TimelineSpanParent[] = []

        while (span.parent) {
            const parent = spans.find(element => element.id === span.parent)

            if (!parent) {
                return parents
            }

            if (parent.e && parent.e.length) {
                for (const e of parent.e) {
                    const event = e

                    if (this.contains(event, span)) {
                        event.span = parent
                        parents.push({ span: event.span, event })
                    }
                }
            }

            parents.push({ span: parent })
            span = parent
        }

        return parents
    }

    public filterTicks = (ticks: object, spans: TimelineSpan[]): string[] =>
        (Object.entries(
            this.pick(
                ticks,
                spans.map(x => x.tick).filter((value, index, sourceArray) => sourceArray.indexOf(value) === index),
            ),
        ) as any)
            .flat()
            .filter(x => x.id !== undefined) || []

    private pick = (object, keys) =>
        keys.reduce((obj, key) => {
            if (object && Object.prototype.hasOwnProperty.call(object, key)) {
                obj[key] = object[key]
            }
            return obj
        }, {})

    private contains = (a: TimelineSpan | TimeLineEvent, b: TimelineSpan | TimeLineEvent): boolean =>
        b.cs >= a.cs && b.cs + b.wt <= a.cs + a.wt

    private isRepeatSpan = (span: TimelineSpan) => span.annotations && 'repeat_count' in span.annotations
}

export default SpanService
